import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";

import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import { makeProfileAvatarUrl } from "~utils/url";

import {
  participantDisplayName,
  participantInitials,
  type ParticipantRow,
  STATUS_OPTIONS,
  statusLabelForKey,
} from "./emergencyCommandCenterShared";

/** Sentinel value for dropdown; saved as exact GPS (`reportedLatitude` / `reportedLongitude`), no map id */
export const MY_LOCATION_MAP_VALUE = "__my_location__";

export interface EmergencyMapLocationOption {
  id: string;
  isActive?: boolean;
  label: string;
  latitude?: number | null;
  longitude?: number | null;
  polygonGeoJson?: string | null;
}

export interface EmergencyParticipantStatusDialogProps {
  open: boolean;
  onClose: () => void;
  participant: ParticipantRow | null;
  /** Map locations for optional location select (active only). Include lat/lng / polygon for “My location” snapping. */
  mapLocations: EmergencyMapLocationOption[];
  mapLocationsLoading?: boolean;
  onSave: (payload: {
    bubbleMapLocationId: string | null;
    statusKey: string;
    reportedLatitude: number | null;
    reportedLongitude: number | null;
  }) => Promise<void>;
  saving?: boolean;
  errorMessage?: string | null;
  /** Shown next to the title (e.g. "You" chip is handled by parent if needed). */
  title?: string;
}

type GeolocationState = "idle" | "prompting" | "granted" | "denied" | "unavailable";

export function EmergencyParticipantStatusDialog({
  errorMessage,
  mapLocations,
  mapLocationsLoading = false,
  onClose,
  onSave,
  open,
  participant,
  saving = false,
  title = "Update status",
}: EmergencyParticipantStatusDialogProps) {
  const [statusKey, setStatusKey] = useState<string>("not-reported");
  const [mapLocationId, setMapLocationId] = useState<string>("");
  const [geoState, setGeoState] = useState<GeolocationState>("idle");
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const myLocationDefaultAppliedRef = useRef(false);

  useEffect(() => {
    if (open && participant) {
      setStatusKey(participant.statusKey ?? "not-reported");
      const named = participant.bubbleMapLocationId?.trim();
      if (named) {
        setMapLocationId(named);
      } else if (
        typeof participant.reportedLatitude === "number" &&
        typeof participant.reportedLongitude === "number"
      ) {
        setMapLocationId(MY_LOCATION_MAP_VALUE);
      } else {
        setMapLocationId("");
      }
      setResolveError(null);
      myLocationDefaultAppliedRef.current = false;
    }
  }, [open, participant]);

  useEffect(() => {
    if (!open) {
      setGeoState("idle");
      setUserCoords(null);
      return;
    }

    if (!navigator.geolocation) {
      setGeoState("unavailable");
      return;
    }

    setGeoState("prompting");
    setUserCoords(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoState("granted");
      },
      () => {
        setGeoState("denied");
        setUserCoords(null);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 25_000 },
    );
  }, [open]);

  useEffect(() => {
    if (geoState !== "denied" && geoState !== "unavailable") {
      return;
    }
    setMapLocationId((prev) => {
      if (prev !== MY_LOCATION_MAP_VALUE) {
        return prev;
      }
      const hasStored =
        participant &&
        typeof participant.reportedLatitude === "number" &&
        typeof participant.reportedLongitude === "number" &&
        !participant.bubbleMapLocationId?.trim();
      return hasStored ? prev : "";
    });
  }, [geoState, participant]);

  useEffect(() => {
    if (!open || !participant) {
      return;
    }
    if (geoState !== "granted" || !userCoords || myLocationDefaultAppliedRef.current) {
      return;
    }
    if (participant.bubbleMapLocationId?.trim()) {
      myLocationDefaultAppliedRef.current = true;
      return;
    }
    setMapLocationId(MY_LOCATION_MAP_VALUE);
    myLocationDefaultAppliedRef.current = true;
  }, [open, participant, geoState, userCoords]);

  const handleSave = useCallback(async () => {
    if (!participant?.userId) {
      return;
    }
    setResolveError(null);
    const raw = mapLocationId.trim();
    if (raw === MY_LOCATION_MAP_VALUE) {
      const lat = userCoords?.lat ?? participant.reportedLatitude;
      const lng = userCoords?.lng ?? participant.reportedLongitude;
      if (typeof lat !== "number" || typeof lng !== "number") {
        setResolveError("Your position is not available. Allow location access or choose a map location.");
        return;
      }
      await onSave({
        bubbleMapLocationId: null,
        reportedLatitude: lat,
        reportedLongitude: lng,
        statusKey,
      });
      return;
    }
    await onSave({
      bubbleMapLocationId: raw ? raw : null,
      reportedLatitude: null,
      reportedLongitude: null,
      statusKey,
    });
  }, [
    mapLocationId,
    onSave,
    participant?.userId,
    participant?.reportedLatitude,
    participant?.reportedLongitude,
    statusKey,
    userCoords,
  ]);

  const hasStoredGps =
    !!participant &&
    typeof participant.reportedLatitude === "number" &&
    typeof participant.reportedLongitude === "number" &&
    !participant.bubbleMapLocationId?.trim();
  const showMyLocationOption = (geoState === "granted" && userCoords !== null) || hasStoredGps;
  const mapLocationSelectValue = mapLocationId === MY_LOCATION_MAP_VALUE && !showMyLocationOption ? "" : mapLocationId;

  const p = participant;
  const roles = (p?.bubbleRoles ?? []).filter(Boolean);

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {p ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack alignItems="center" direction="column" spacing={1}>
              <Avatar alt="" src={p.userId ? makeProfileAvatarUrl(p.userId) : undefined} sx={{ height: 88, width: 88 }}>
                {participantInitials(p)}
              </Avatar>
              <Typography textAlign="center" variant="subtitle1">
                {participantDisplayName(p)}
              </Typography>
              {p.phoneNumber?.trim() ? (
                <PhoneWithCopyIcon phoneNumber={p.phoneNumber.trim()} showPhoneIcon={false} />
              ) : (
                <Typography color="text.secondary" variant="body2">
                  —
                </Typography>
              )}
              <Typography color="text.secondary" variant="body2">
                <strong>Role:</strong> {roles.length > 0 ? roles.join(", ") : "—"}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                <strong>Current status:</strong> {statusLabelForKey(p.statusKey)}
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="emergency-dialog-status-label">Status</InputLabel>
              <Select
                disabled={saving}
                label="Status"
                labelId="emergency-dialog-status-label"
                onChange={(e) => setStatusKey(String(e.target.value))}
                value={statusKey}
              >
                {STATUS_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="emergency-dialog-loc-label">Map location (optional)</InputLabel>
              <Select
                disabled={saving || mapLocationsLoading}
                label="Map location (optional)"
                labelId="emergency-dialog-loc-label"
                onChange={(e) => {
                  setMapLocationId(String(e.target.value));
                  setResolveError(null);
                }}
                value={mapLocationSelectValue}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {showMyLocationOption ? <MenuItem value={MY_LOCATION_MAP_VALUE}>My location (GPS)</MenuItem> : null}
                {mapLocations
                  .filter((l) => l.isActive !== false)
                  .map((l) => (
                    <MenuItem key={l.id} value={l.id}>
                      {l.label}
                    </MenuItem>
                  ))}
              </Select>
              {geoState === "prompting" ? (
                <FormHelperText sx={{ alignItems: "center", display: "flex", gap: 1, mt: 1 }}>
                  <CircularProgress size={14} />
                  Requesting location access…
                </FormHelperText>
              ) : null}
              {geoState === "denied" || geoState === "unavailable" ? (
                <FormHelperText>
                  {geoState === "unavailable"
                    ? "This browser does not support location. Choose a map location from the list or None."
                    : "Location permission not granted. Choose a map location from the list or None."}
                </FormHelperText>
              ) : null}
              {mapLocationId === MY_LOCATION_MAP_VALUE && showMyLocationOption ? (
                <FormHelperText>
                  Your device GPS coordinates are saved as reported (WGS84). Staff see your pin at this position on the
                  map.
                </FormHelperText>
              ) : null}
            </FormControl>

            {errorMessage ? (
              <Typography color="error" variant="body2">
                {errorMessage}
              </Typography>
            ) : null}
            {resolveError ? (
              <Typography color="error" variant="body2">
                {resolveError}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={
            saving ||
            !p?.userId ||
            (mapLocationsLoading && mapLocationId !== "" && mapLocationId !== MY_LOCATION_MAP_VALUE)
          }
          onClick={() => void handleSave()}
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : null}
          variant="contained"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
